import { BarChart3, Table2 } from 'lucide-react';
import * as React from 'react';
import {
  Area,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ComposedChart,
  LabelList,
  Line,
  ResponsiveContainer,
  Tooltip as RTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { cn } from '../../lib/cn.js';
import { EMPTY, formatNumber, formatPercent } from '../../lib/format.js';
import { cssVar } from '../../tokens/index.js';
import { Card, CardHeader } from '../layout/card.js';
import { Button } from '../primitives/button.js';
import { EmptyState, Skeleton } from '../primitives/states.js';

export interface ChartLegendItem {
  label: string;
  color: string;
  dashed?: boolean;
}

export interface ChartTable {
  columns: string[];
  rows: Array<Array<string | number>>;
}

export interface ChartContainerProps {
  title: string;
  description?: React.ReactNode;
  legend?: ChartLegendItem[];
  /** Accessible tabular equivalent. Always rendered for screen readers; can be shown visually. */
  table: ChartTable;
  children: React.ReactNode;
  height?: number;
  loading?: boolean;
  /** When true, renders the empty state instead of the chart. */
  empty?: boolean;
  emptyText?: string;
  actions?: React.ReactNode;
  footer?: React.ReactNode;
  className?: string;
}

export function ChartContainer({
  title,
  description,
  legend,
  table,
  children,
  height = 280,
  loading,
  empty,
  emptyText = 'No hay datos para este periodo.',
  actions,
  footer,
  className,
}: ChartContainerProps) {
  const [showTable, setShowTable] = React.useState(false);
  const titleId = React.useId();
  return (
    <Card aria-labelledby={titleId} className={cn('flex flex-col', className)}>
      <CardHeader
        id={titleId}
        title={title}
        description={description}
        actions={
          <>
            {actions}
            <Button
              variant="ghost"
              size="sm"
              aria-pressed={showTable}
              onClick={() => setShowTable((s) => !s)}
              leadingIcon={showTable ? <BarChart3 aria-hidden /> : <Table2 aria-hidden />}
            >
              {showTable ? 'Ver gráfica' : 'Ver tabla'}
            </Button>
          </>
        }
      />
      {legend && legend.length > 0 && !showTable ? (
        <ul className="mb-3 flex flex-wrap gap-x-4 gap-y-1" aria-label="Leyenda">
          {legend.map((l) => (
            <li key={l.label} className="inline-flex items-center gap-1.5 text-helper text-text-secondary">
              <svg width="18" height="8" aria-hidden>
                <line x1="1" y1="4" x2="17" y2="4" stroke={l.color} strokeWidth="2.5" strokeDasharray={l.dashed ? '4 3' : undefined} strokeLinecap="round" />
              </svg>
              {l.label}
            </li>
          ))}
        </ul>
      ) : null}
      {loading ? (
        <Skeleton label={`Cargando ${title}`} style={{ height }} className="w-full" />
      ) : empty ? (
        <div style={{ minHeight: height }} className="flex items-center justify-center">
          <EmptyState compact title="Sin datos" description={emptyText} />
        </div>
      ) : (
        <>
          <div aria-hidden className={cn(showTable && 'hidden')} style={{ height }}>
            {children}
          </div>
          <div className={cn(showTable ? 'max-h-[360px] overflow-auto rounded-menu border border-border-subtle' : 'sr-only')}>
            <table className="w-full text-left text-body">
              <caption className="sr-only">{title} — tabla de datos</caption>
              <thead className="sticky top-0 bg-surface-subtle">
                <tr>
                  {table.columns.map((c) => (
                    <th key={c} scope="col" className="px-3 py-2 text-helper font-semibold text-text-secondary">
                      {c}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {table.rows.map((r, i) => (
                  <tr key={i} className="border-t border-border-subtle">
                    {r.map((cell, j) => (
                      <td key={j} className={cn('cm-tabular px-3 py-2 text-text-primary', j > 0 && 'text-right')}>
                        {cell}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
      {footer ? <div className="mt-3 text-helper text-text-secondary">{footer}</div> : null}
    </Card>
  );
}

function TooltipBox({ title, lines }: { title: string; lines: Array<{ label: string; value: string; color: string; dashed?: boolean }> }) {
  return (
    <div className="rounded-menu border border-border-subtle bg-surface-default px-3 py-2 shadow-overlay">
      <p className="mb-1 text-helper font-semibold text-text-primary">{title}</p>
      {lines.map((l) => (
        <p key={l.label} className="cm-tabular flex items-center gap-2 text-helper text-text-secondary">
          <svg width="12" height="6" aria-hidden>
            <line x1="1" y1="3" x2="11" y2="3" stroke={l.color} strokeWidth="2.5" strokeDasharray={l.dashed ? '3 2' : undefined} />
          </svg>
          {l.label}: <span className="font-semibold text-text-primary">{l.value}</span>
        </p>
      ))}
    </div>
  );
}

export interface TimeSeriesPoint {
  /** Category label on the x axis (already formatted, e.g. "12 sep"). */
  label: string;
  /** null = no measurement (gap), never plotted as zero. */
  value: number | null;
  previous?: number | null;
}

export interface TimeSeriesChartProps {
  data: readonly TimeSeriesPoint[];
  seriesLabel: string;
  previousLabel?: string;
  valueFormatter?: (v: number) => string;
  /** Axis tick formatter (shorter). Defaults to valueFormatter. */
  axisFormatter?: (v: number) => string;
  height?: number;
}

/** Primary series as a line with a 12%→0 gradient area; previous period dashed slate. */
export function TimeSeriesChart({ data, seriesLabel, previousLabel, valueFormatter = (v) => formatNumber(v), axisFormatter, height = 280 }: TimeSeriesChartProps) {
  const gradId = React.useId().replace(/:/g, '');
  const hasPrev = data.some((d) => d.previous !== undefined && d.previous !== null);
  return (
    <ResponsiveContainer width="100%" height={height}>
      <ComposedChart data={data as TimeSeriesPoint[]} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
        <defs>
          <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={cssVar.primary} stopOpacity={0.12} />
            <stop offset="100%" stopColor={cssVar.primary} stopOpacity={0} />
          </linearGradient>
        </defs>
        <CartesianGrid vertical={false} stroke={cssVar.grid} />
        <XAxis dataKey="label" tickLine={false} axisLine={false} tick={{ fill: cssVar.axis, fontSize: 12 }} minTickGap={16} />
        <YAxis
          tickLine={false}
          axisLine={false}
          width={56}
          tick={{ fill: cssVar.axis, fontSize: 12 }}
          tickFormatter={(v: number) => (axisFormatter ?? valueFormatter)(v)}
        />
        <RTooltip
          cursor={{ stroke: cssVar.grid }}
          content={({ active, payload, label }) =>
            active && payload ? (
              <TooltipBox
                title={String(label)}
                lines={[
                  {
                    label: seriesLabel,
                    value: typeof payload.find((p) => p.dataKey === 'value')?.value === 'number' ? valueFormatter(payload.find((p) => p.dataKey === 'value')?.value as number) : EMPTY,
                    color: cssVar.primary,
                  },
                  ...(hasPrev && previousLabel
                    ? [
                        {
                          label: previousLabel,
                          value: typeof payload.find((p) => p.dataKey === 'previous')?.value === 'number' ? valueFormatter(payload.find((p) => p.dataKey === 'previous')?.value as number) : EMPTY,
                          color: cssVar.chartPrevious,
                          dashed: true,
                        },
                      ]
                    : []),
                ]}
              />
            ) : null
          }
        />
        {hasPrev ? (
          <Line type="monotone" dataKey="previous" stroke={cssVar.chartPrevious} strokeWidth={1.75} strokeDasharray="5 4" dot={false} connectNulls={false} isAnimationActive={false} />
        ) : null}
        <Area type="monotone" dataKey="value" stroke={cssVar.primary} strokeWidth={2.25} fill={`url(#${gradId})`} dot={false} activeDot={{ r: 4 }} connectNulls={false} isAnimationActive={false} />
      </ComposedChart>
    </ResponsiveContainer>
  );
}

export interface DistributionItem {
  key: string;
  label: string;
  count: number;
}

export interface DistributionBarChartProps {
  data: readonly DistributionItem[];
  /** Stable category order → color index. Keys not listed get the last series color. */
  categories?: readonly string[];
  height?: number;
  showPercent?: boolean;
}

/** Stable category → color mapping; keep the categories list constant across screens. */
export function categoryColor(key: string, categories?: readonly string[]): string {
  const idx = categories ? categories.indexOf(key) : -1;
  const palette = cssVar.chart;
  return palette[(idx >= 0 ? idx : palette.length - 1) % palette.length] as string;
}

export function DistributionBarChart({ data, categories, height, showPercent = true }: DistributionBarChartProps) {
  const total = data.reduce((s, d) => s + d.count, 0);
  const rows = data.map((d) => ({ ...d, text: showPercent && total > 0 ? `${formatNumber(d.count)} · ${formatPercent(d.count / total, 0)}` : formatNumber(d.count) }));
  const h = height ?? Math.max(120, rows.length * 40 + 16);
  return (
    <ResponsiveContainer width="100%" height={h}>
      <BarChart data={rows} layout="vertical" margin={{ top: 4, right: 88, bottom: 4, left: 0 }} barCategoryGap={10}>
        <XAxis type="number" hide domain={[0, 'dataMax']} />
        <YAxis type="category" dataKey="label" width={128} tickLine={false} axisLine={false} tick={{ fill: cssVar.axis, fontSize: 12 }} />
        <Bar dataKey="count" radius={[0, 6, 6, 0]} isAnimationActive={false} minPointSize={2}>
          {rows.map((r) => (
            <Cell key={r.key} fill={categoryColor(r.key, categories)} />
          ))}
          <LabelList dataKey="text" position="right" style={{ fill: 'var(--cm-text-primary)', fontSize: 12, fontVariantNumeric: 'tabular-nums' }} />
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}
